// Authed fetch for the calls that cannot go through openapi-fetch: multipart
// uploads, whose body is a FormData the generated client has no schema for.
//
// Everything the openapi-fetch middleware does for a normal call has to happen
// here too, or the upload is the one request in the app that authenticates
// differently from the rest:
//
//   - `credentials: "include"` so the HttpOnly session_id cookie travels. The
//     FE and the API are on different origins in every environment we deploy
//     (gnpf-dev.* → api-gnpf-dev.*, :5173 → :8080), so the default
//     "same-origin" silently drops the cookie and the BE sees an anonymous
//     request.
//   - Authorization only in bearer mode. Under cookie-session the store's
//     accessToken is empty after a PIN login by design — the BE snapshot fast
//     path deliberately returns no Casdoor token — so a bearer-only uploader
//     has no credential at all.
//   - 401 → refresh → retry, sharing the single in-flight refresh.

import { useAuthV2 } from "@/lib/authv2/store";
import { ensureFreshSessionForRequest, refreshOn401 } from "@/lib/authv2/refreshInterceptor";
import { signOutIfStaffInactive } from "@/lib/authv2/staffInactive";
import { isNetworkFailure, NETWORK_ERROR_MESSAGE } from "./error-message";

const authMode = import.meta.env.VITE_AUTH_MODE ?? "cookie-session";

// Single owner of the credential rule; the openapi-fetch middleware imports
// this rather than re-deriving it.
export function shouldSendBearer(): boolean {
  return authMode !== "cookie-session";
}

/**
 * Network failure of a `postFormAuthed(..., { reportBodySent: true })` call.
 * `bodySent` — the browser had sent the WHOLE body before the connection died,
 * so the server may have received and saved it: resending could store a
 * duplicate. False means the server cannot have read a complete request.
 */
export class FormSendError extends Error {
  readonly bodySent: boolean;
  constructor(bodySent: boolean, cause: unknown) {
    super(NETWORK_ERROR_MESSAGE, { cause });
    this.name = "FormSendError";
    this.bodySent = bodySent;
  }
}

function responseHeaders(request: XMLHttpRequest): Headers {
  const headers = new Headers();
  for (const line of request.getAllResponseHeaders().trim().split(/[\r\n]+/)) {
    const at = line.indexOf(":");
    if (at > 0) headers.append(line.slice(0, at).trim(), line.slice(at + 1).trim());
  }
  return headers;
}

// fetch cannot tell whether the body left the browser before the connection
// died; XHR's upload "load" event can. Same credentials as the fetch path.
function sendFormXhr(url: string, headers: Record<string, string>, body: FormData): Promise<Response> {
  return new Promise((resolve, reject) => {
    const request = new XMLHttpRequest();
    let bodySent = false;
    request.open("POST", url);
    request.withCredentials = true;
    for (const [name, value] of Object.entries(headers)) request.setRequestHeader(name, value);
    request.upload.onload = () => {
      bodySent = true;
    };
    request.onload = () => {
      const { status } = request;
      const nullBody = status === 204 || status === 205 || status === 304;
      resolve(
        new Response(nullBody ? null : request.responseText, {
          status,
          statusText: request.statusText,
          headers: responseHeaders(request),
        }),
      );
    };
    const fail = () => reject(new FormSendError(bodySent, new TypeError("Failed to fetch")));
    request.onerror = fail;
    request.onabort = fail;
    request.ontimeout = fail;
    request.send(body);
  });
}

/**
 * POST a FormData body with the session credential attached, retrying once
 * through /auth/refresh when the BE answers 401.
 *
 * `body` is re-sent as-is on retry: FormData is not consumed by fetch the way
 * a Request stream is, so the same instance can be posted twice.
 *
 * `reportBodySent`: send through XHR and throw `FormSendError` on network
 * failure, so a non-idempotent upload can tell whether resending is safe.
 */
export async function postFormAuthed(
  url: string,
  body: FormData,
  options: { reportBodySent?: boolean } = {},
): Promise<Response> {
  const send = async () => {
    const headers: Record<string, string> = {};
    const { accessToken } = useAuthV2.getState();
    if (accessToken && shouldSendBearer()) headers["Authorization"] = `Bearer ${accessToken}`;
    // Content-Type is left unset on purpose — the browser has to add the
    // multipart boundary itself.
    if (options.reportBodySent) return sendFormXhr(url, headers, body);
    try {
      return await fetch(url, { method: "POST", headers, body, credentials: "include" });
    } catch (error) {
      // Same wording as the openapi-fetch client's network middleware.
      if (isNetworkFailure(error)) throw new Error(NETWORK_ERROR_MESSAGE, { cause: error });
      throw error;
    }
  };
  await ensureFreshSessionForRequest();
  const res = await refreshOn401(await send(), send);
  // QĐ 0412: cùng luật đăng xuất người đã nghỉ với client openapi-fetch.
  await signOutIfStaffInactive(res);
  return res;
}

/**
 * GET with the same credential rules as postFormAuthed — for large binary
 * downloads the caller reads as a stream (progress bar), which openapi-fetch
 * would buffer whole before handing back.
 */
export async function getAuthed(url: string, signal?: AbortSignal): Promise<Response> {
  const send = async () => {
    const headers: Record<string, string> = {};
    const { accessToken } = useAuthV2.getState();
    if (accessToken && shouldSendBearer()) headers["Authorization"] = `Bearer ${accessToken}`;
    try {
      return await fetch(url, {
        method: "GET",
        headers,
        credentials: "include",
        signal: signal ?? null,
      });
    } catch (error) {
      if (isNetworkFailure(error)) throw new Error(NETWORK_ERROR_MESSAGE, { cause: error });
      throw error;
    }
  };
  await ensureFreshSessionForRequest();
  const res = await refreshOn401(await send(), send);
  // QĐ 0412: cùng luật đăng xuất người đã nghỉ với client openapi-fetch.
  await signOutIfStaffInactive(res);
  return res;
}
